import ManagedTable, { ManagedColumn, ManagedColumnExtra, ManagedTableProps } from 'app/common/table/ManagedTable';
import TimeUtils from 'app/common/util/TimeUtils';
import FlightUtils from 'app/common/util/FlightUtils';
import React, { CSSProperties } from 'react';
import Utils from 'app/common/util/Utils';
import { TableUtils } from 'app/common/util/TableUtils';
import KeyUtils from 'app/common/util/KeyUtils';
import useWindowDimensions from '../../common/hooks/useWindowDimensions';
import { CheckCircleFilled } from '@ant-design/icons';
import './ReroutedFlightTable.less';
import { ReroutedFlightDto } from 'app/model/SOL49';

const REROUTING_APPLIED_STYLE: CSSProperties = { color: 'red', fontWeight: 'bold' };

function toReroutingAppliedStyle(value: React.ReactNode, row: ReroutedFlightDto): CSSProperties {
    const style: CSSProperties = !row.reroutingApplied ? REROUTING_APPLIED_STYLE : undefined;
    return style;
}

export interface ReroutedFlightColumnExtra extends ManagedColumnExtra<ReroutedFlightDto> {}

/* Rerouted Flight Columns & Rerouted Flight Table definitions */
export class ReroutedFlightColumn extends ManagedColumn<ReroutedFlightDto> {
    constructor(
        header: string,
        width: string,
        getCellValue: (object: ReroutedFlightDto) => any,
        extra?: ReroutedFlightColumnExtra,
    ) {
        super(header, width, getCellValue, extra);
    }
}

export const REROUTED_FLIGHT_SELECTED_COLUMN = new ReroutedFlightColumn(
    '',
    '2em',
    object => (
        <div className={'check-circle'} style={{ textAlign: 'center' }}>
            <CheckCircleFilled />
        </div>
    ),
    { name: 'selected' },
);

export class StandardReroutingFlightColumn {
    public static readonly ADEP = new ReroutedFlightColumn(
        'ADEP',
        '4em',
        object => object.flightId.flightKeys && object.flightId.flightKeys.adep,
    );
    public static readonly ADES = new ReroutedFlightColumn(
        'ADES',
        '4em',
        object => object.flightId.flightKeys && object.flightId.flightKeys.ades,
    );
    public static readonly APPLIED = new ReroutedFlightColumn('RR', '4em', object =>
        object.reroutingApplied ? 'Y' : 'N',
    );
    public static readonly ARCID = new ReroutedFlightColumn(
        'ARCID',
        '6em',
        object => object.flightId.flightKeys && object.flightId.flightKeys.callsign,
    );
    public static readonly ATYPE = new ReroutedFlightColumn('ATY', '4em', object => object.aircraftType);
    public static readonly DELAY = new ReroutedFlightColumn(
        'DELAY',
        '5em',
        object => object.delayInMs && object.delayInMs !== -1 && (object.delayInMs / TimeUtils.MIN_TO_MS).toString(),
        { comparator: Utils.compareStrNumbers },
    );
    public static readonly DELTA_EET = new ReroutedFlightColumn(
        'DEET',
        '5em',
        object =>
            object.deltas?.newEetInMs &&
            object.deltas?.origEetInMs &&
            Utils.toSignedString((object.deltas?.newEetInMs - object.deltas?.origEetInMs) / TimeUtils.MIN_TO_MS, 1),
    );
    public static readonly DELTA_FUEL_CONSUMPTION = new ReroutedFlightColumn(
        'DFuel',
        '5em',
        object =>
            object.deltas?.newFuelConsumptionInKg &&
            object.deltas?.origFuelConsumptionInKg &&
            Utils.toSignedString(object.deltas?.newFuelConsumptionInKg - object.deltas?.origFuelConsumptionInKg),
    );
    public static readonly DELTA_ROUTE_CHARGES = new ReroutedFlightColumn(
        'DCharges',
        '5em',
        object =>
            object.deltas?.newRouteChargesInEuros &&
            object.deltas?.origRouteChargesInEuros &&
            Utils.toSignedString(object.deltas?.newRouteChargesInEuros - object.deltas?.origRouteChargesInEuros),
    );
    public static readonly DELTA_ROUTE_LEN = new ReroutedFlightColumn(
        'DLength',
        '5em',
        object =>
            object.deltas?.newRouteLengthInNm &&
            object.deltas?.origRouteLengthInNm &&
            Utils.toSignedString(object.deltas?.newRouteLengthInNm - object.deltas?.origRouteLengthInNm),
    );
    public static readonly IOBT = new ReroutedFlightColumn('IOBT', '5em', object =>
        TimeUtils.formatDDHHMM(object.flightId?.flightKeys?.eobt),
    );
    public static readonly MPR = new ReroutedFlightColumn('MPR', '7em', object => object.mostPenalisingRegulation);
    public static readonly REASON = new ReroutedFlightColumn('REASON', '75em', object => object.reason, {
        cellStyle: toReroutingAppliedStyle,
    });
}
export class ReroutedFlightTableBase extends ManagedTable<ReroutedFlightDto, ManagedTableProps<ReroutedFlightDto>> {
    static getUpdatedSingleSelection<T extends ReroutedFlightDto>(selection: T, data: Array<T>) {
        return TableUtils.getUpdatedSingleSelection(
            selection,
            data,
            KeyUtils.FLIGHT_KEY_PROVIDER,
            FlightUtils.isSameFlight,
        );
    }

    static getUpdatedMultipleSelection<T extends ReroutedFlightDto>(selection: Array<T>, data: Array<T>) {
        return TableUtils.getUpdatedMultipleSelection(
            selection,
            data,
            KeyUtils.FLIGHT_KEY_PROVIDER,
            FlightUtils.isSameFlight,
        );
    }
}

// I have had to adapt ManagedTable so that it accepts a font size.
// VirtualTable that is behind only accepts column width in "px", whereas our column definition is in "em".
// This usually is not a problem when font size is fixed, but in our case, font size depends on the viewport width.
// When the viewport is resized, the new font size is provided to the flight table, that will adapt column width accordingly.
const ReroutedFlightTable = (props: ManagedTableProps<ReroutedFlightDto>) => {
    const { width } = useWindowDimensions();
    const fontSize = (0.42 * width) / 100;

    return <ReroutedFlightTableBase fontSize={fontSize} {...props} />;
};

export default ReroutedFlightTable;
